/**
 * Where a live side match stands.
 *
 * A sibling of `Nassau.tsx`, not a mode of it, and the difference is not
 * cosmetic. That screen is a stepper: the cards are finished, the press
 * decisions are being made at the table, and it holds every one of them in
 * `useState` and refuses to advance until the question on screen is answered.
 * None of that is true here. Nothing is answered — `none` and `auto` store no
 * decisions — there is no cursor to advance, and the man reading it is standing
 * on the 12th tee wanting one number.
 *
 * So it is a summary, and it derives everything from the same merged store the
 * rest of the app reads. A corrected score on somebody else's phone repairs this
 * board on the next poll, with no other write anywhere.
 */

import { useMemo, useState } from 'react'
import type { NassauWalk } from '../engine/nassau'
import {
  nettedSideMoney,
  runSideMatch,
  settle,
  sliceFor,
  type SideMatch,
  type SideSettlement,
} from '../engine/sideMatch'
import type { HoleScore, Tournament } from '../engine/types'
import { playerName } from '../select'
import {
  mergedConfirmations,
  mergedScores,
  mergedSideMatches,
  useStore,
  voidSideMatch,
} from '../store'
import { Bar, Empty, SectionRule } from '../ui'

/**
 * Dollars, from cents, for reading only. Nothing is ever computed from this.
 *
 * A round number loses its decimals, because "$20" is what was agreed and
 * "$20.00" reads like an invoice. Anything else keeps both of them, since "$12.5"
 * is not a way of writing money.
 */
export const dollars = (cents: number): string => {
  const whole = Math.abs(cents) / 100
  const places = Number.isInteger(whole) ? 0 : 2
  return `$${whole.toLocaleString(undefined, {
    minimumFractionDigits: places,
    maximumFractionDigits: places,
  })}`
}

/**
 * "thru 7" — and the honesty in it.
 *
 * `playedThrough` is the index of the LAST hole with both scores, not a count
 * (nassau.ts:282): with holes 1, 2 and 4 in it reports 4 while three were
 * played. When the two disagree this says both, because "2 up thru 4" over three
 * holes is a claim nobody can check against the card in their hand.
 */
export function thruLabel(walk: NassauWalk): string {
  const scored = walk.steps.filter((s) => s.outcome !== null).length
  if (walk.playedThrough === 0) return 'not started'
  if (scored === walk.playedThrough) return `thru ${walk.playedThrough}`
  return `thru ${walk.playedThrough}, ${scored} scored`
}

/**
 * A bet's margin with a name against it.
 *
 * Read off `status` rather than off `up`, because `status` carries the closeout
 * margin: a bet won 5&4 keeps saying "5 DN" while the loser takes the dead holes
 * back, and the raw tally drifts toward level. Which side that margin belongs to
 * is in the same string — UP is always A, DN always B.
 */
export function betLabel(
  status: string,
  nameA: string,
  nameB: string,
  closed = false,
): string {
  if (status === 'AS') return closed ? 'Halved' : 'AS'
  const [margin, direction] = status.split(' ')
  const name = direction === 'UP' ? nameA : nameB
  // "Cramer 1" made Scott stop and count. "Cramer up 1" is the sentence a
  // caddie would say; a finished bet says what happened rather than a state.
  return closed ? `${name} won ${margin} up` : `${name} up ${margin}`
}

/** The total, from one man's point of view, for a masthead. */
export function standingLabel(up: number, nameA: string, nameB: string): string {
  return up === 0 ? 'All square' : `${up > 0 ? nameA : nameB} ${Math.abs(up)} up`
}

export interface SideStanding {
  slice: ReturnType<typeof sliceFor>
  walk: NassauWalk
  settlement: SideSettlement
}

/**
 * Everything derived for one match, in one place and one way.
 *
 * The strip on the Score tab, the board and the netting all come through here.
 * Three surfaces deriving the same match three ways is how the line on the Score
 * tab ends up disagreeing with the board it taps through to.
 */
export function standingFor(
  doc: Tournament,
  match: SideMatch,
  scores: HoleScore[],
  confirmations: { roundId: string; groupId: string }[],
): SideStanding | null {
  const slice = sliceFor(doc, match.roundIds, scores)
  if (slice.holes.length === 0) return null
  /*
   * A bet whose rounds have been taken out from under it does not settle.
   *
   * The holes are re-read on every render, so removing one nine of an eighteen
   * silently shortened the card: front, back and total collapsed into one bet
   * and "$200 you owe" became "$50 you owe · so far", which reads exactly like
   * an ordinary bet in progress. It is not this screen's place to guess what
   * two men agreed; it is its place to stop quoting a number.
   */
  if (slice.missingRoundIds.length > 0) return null
  const walk = runSideMatch({
    mode: match.pressMode,
    holes: slice.holes,
    grossA: slice.grossFor(match.playerA),
    grossB: slice.grossFor(match.playerB),
    strokes: match.strokes,
    amountCents: match.amountCents,
    nameA: playerName(doc, match.playerA),
    nameB: playerName(doc, match.playerB),
  })
  return { slice, walk, settlement: settle(doc, match, walk, confirmations) }
}

/** The same thing, memoised, for a component that renders exactly one match. */
export function useSideStanding(
  doc: Tournament,
  match: SideMatch | undefined,
): SideStanding | null {
  const scores = useStore(mergedScores)
  const confirmations = useStore(mergedConfirmations)
  return useMemo(
    () => (match ? standingFor(doc, match, scores, confirmations) : null),
    [doc, match, scores, confirmations],
  )
}

export function SideBoard({
  doc,
  matchId,
  onBack,
}: {
  doc: Tournament
  matchId: string
  onBack: () => void
}) {
  const matches = useStore(mergedSideMatches)
  const me = useStore((s) => s.session?.playerId ?? '')
  const match = matches.find((m) => m.id === matchId)
  const standing = useSideStanding(doc, match)

  if (!match || !standing) {
    return (
      <>
        <Bar kicker="Side match" title="Not here" />
        <Empty>
          That match is not on this phone. It may have been called off, or the week may
          have been reset.
        </Empty>
        <div className="px-4 pb-8">
          <button
            type="button"
            onClick={onBack}
            className="bc-tap w-full border-2 border-ink bc-label py-3"
          >
            Back
          </button>
        </div>
      </>
    )
  }

  const { walk, settlement } = standing
  const nameA = playerName(doc, match.playerA)
  const nameB = playerName(doc, match.playerB)
  const total = walk.standings.find((s) => s.bet.id === 'total')
  const mine = match.playerA === me || match.playerB === me
  const first = standing.slice.roundLabels[0] ?? ''
  const last = standing.slice.roundLabels.at(-1) ?? ''

  // Live bets first, then the ones that are over: on the 12th tee the front is
  // history and the press that just went on is the news.
  const bets = [...walk.standings].sort((a, b) => {
    const done = (x: typeof a) => (x.closed || x.decided ? 1 : 0)
    return done(a) - done(b) || a.bet.startIndex - b.bet.startIndex
  })

  return (
    <>
      <Bar
        kicker={`${nameA} v ${nameB}`}
        title={total ? standingLabel(total.up, nameA, nameB) : 'Side match'}
        sub={`${thruLabel(walk)} · ${dollars(match.amountCents)} a bet · ${
          match.pressMode === 'auto' ? 'auto presses' : 'no presses'
        }`}
        live={!match.voidedAt}
      />

      {match.voidedAt && (
        <p className="bg-ink text-paper px-4 py-3 bc-label">
          Called off by {playerName(doc, match.voidedBy ?? '')}. This match settles at
          nothing.
        </p>
      )}

      <div className="px-4 py-3 border-b border-rule">
        <p className="text-[13px] text-mute leading-snug">
          {first === last ? first : `${first} + ${last}`}.{' '}
          {match.strokes === 0
            ? 'Level — no strokes either way.'
            : `${match.strokes > 0 ? nameA : nameB} gives ${
                match.strokes > 0 ? nameB : nameA
              } ${Math.abs(match.strokes)}, on the hardest holes of the eighteen.`}{' '}
          Struck by {playerName(doc, match.createdBy)} and frozen there — neither the
          strokes nor the press rule move once a match is live.
        </p>
      </div>

      <SectionRule>Where the money is</SectionRule>
      <ul className="divide-y divide-rule">
        {bets.map((s) => (
          <li key={s.bet.id} className="px-4 py-2.5 flex items-center gap-3">
            <span className="bc-name text-sm flex-1">
              {s.bet.label}
              {/* The match position it started from, not the card's hole number:
                  across two nines the card says "3" twice. */}
              {s.bet.parentId && (
                <span className="text-mute text-[12px] ml-1.5">
                  from hole {s.bet.startIndex + 1}
                </span>
              )}
              {s.closed && <span className="text-mute text-[12px] ml-1.5">final</span>}
              {!s.closed && s.decided && (
                <span className="text-signal text-[12px] ml-1.5">decided</span>
              )}
            </span>
            <span
              className={`bc-num text-base ${
                s.up > 0 ? 'text-ink' : s.up < 0 ? 'text-signal' : 'text-mute'
              }`}
            >
              {betLabel(s.status, nameA, nameB, s.closed)}
            </span>
          </li>
        ))}
      </ul>

      <SectionRule>{settlement.provisional ? 'As it stands' : 'Settled'}</SectionRule>
      <div className="px-4 py-4 bg-ink text-paper">
        <div className="bc-label opacity-70">
          {walk.ledger.aBets} to {nameA} · {walk.ledger.bBets} to {nameB}
          {walk.ledger.halved > 0 && ` · ${walk.ledger.halved} halved`}
          {walk.ledger.open > 0 && ` · ${walk.ledger.open} still open`}
        </div>
        <div className="bc-display text-3xl mt-1">
          {settlement.netCents === 0
            ? 'All square'
            : `${playerName(
                doc,
                settlement.netCents > 0 ? match.playerA : match.playerB,
              )} +${dollars(settlement.netCents)}`}
        </div>
        {/*
          A provisional figure is never presented as a settled one.
          Money in this app comes off cards a foursome has signed for, and a
          Nassau is no different — the number above is what the holes entered so
          far say, not what anybody should send.
        */}
        {(() => {
          /*
           * "Open" for MONEY means undecided, not merely unclosed. The
           * ledger already pays every bet whose winner is known — a total
           * closed out 4-up with two to play is decided, paid in netCents,
           * and re-adding it here printed "+$40 if they hold" on a $20 bet.
           * Found by the review agent, verified against nassau.ts:270.
           */
          const open = bets.filter((x) => x.winner === null)
          if (open.length === 0) return null
          const aLeads = open.filter((x) => x.up > 0).length
          const bLeads = open.filter((x) => x.up < 0).length
          const ifHeld =
            settlement.netCents +
            open.reduce((n, x) => n + Math.sign(x.up) * match.amountCents, 0)
          const held =
            ifHeld === 0
              ? 'all square'
              : `${playerName(doc, ifHeld > 0 ? match.playerA : match.playerB)} +${dollars(Math.abs(ifHeld))}`
          const lean =
            aLeads > 0 && bLeads > 0
              ? `${nameA} up in ${aLeads}, ${nameB} up in ${bLeads} of the open bets`
              : aLeads > 0
                ? `${nameA} up in ${aLeads === open.length ? (open.length === 1 ? 'the open bet' : `all ${open.length} open bets`) : `${aLeads} of the open bets`}`
                : bLeads > 0
                  ? `${nameB} up in ${bLeads === open.length ? (open.length === 1 ? 'the open bet' : `all ${open.length} open bets`) : `${bLeads} of the open bets`}`
                  : null
          if (!lean) return null
          return (
            <p className="text-[13px] opacity-80 leading-snug mt-2">
              {lean} — {held} if they hold.
            </p>
          )
        })()}
        {settlement.provisional && (
          <p className="text-[13px] opacity-80 leading-snug mt-2">
            Not settled —{' '}
            {settlement.unsigned.length > 0
              ? `${settlement.unsigned.length} card${
                  settlement.unsigned.length === 1 ? '' : 's'
                } still to sign`
              : `${walk.ledger.open} bet${walk.ledger.open === 1 ? '' : 's'} still open`}
            . Nobody should send anything off this number yet.
          </p>
        )}
      </div>

      <SideMoney doc={doc} viewerId={me || match.playerA} />

      <p className="px-4 pt-4 text-[13px] text-mute leading-snug">
        Clearing one foursome&rsquo;s card leaves this match standing — it simply
        recomputes to fewer holes played. Only a full reset removes it.
      </p>

      <div className="flex gap-2 px-4 py-4 pb-8">
        <button
          type="button"
          onClick={onBack}
          className="bc-tap flex-1 border-2 border-ink bc-label py-3"
        >
          Back
        </button>
        {mine && !match.voidedAt && <VoidButton matchId={match.id} />}
      </div>
    </>
  )
}

/**
 * Call a match off. Two taps.
 *
 * Either man may, which is the point: a bet one of the two does not want to play
 * is not a bet, and leaving it with whoever struck it means one of them is stuck.
 * It is not undoable from the app, hence the arming tap.
 */
function VoidButton({ matchId }: { matchId: string }) {
  const [armed, setArmed] = useState(false)
  return (
    <button
      type="button"
      onClick={() => (armed ? voidSideMatch(matchId) : setArmed(true))}
      className={`bc-tap flex-1 bc-label py-3 ${
        armed ? 'bg-signal text-paper' : 'border-2 border-rule text-mute'
      }`}
    >
      {armed ? 'Tap again to void' : 'Call it off'}
    </button>
  )
}

/**
 * Side money, netted per pair, kept visibly apart from the tournament ledger.
 *
 * One transfer per pair at the end of the week is how this gets paid, so one row
 * per pair is how it is shown. None of it is in the pot: not in the standings,
 * not in the payouts, not in `scheduledPayout`, and there is no code path from
 * here to any of them.
 */
export function SideMoney({ doc, viewerId }: { doc: Tournament; viewerId: string }) {
  const matches = useStore(mergedSideMatches)
  const scores = useStore(mergedScores)
  const confirmations = useStore(mergedConfirmations)

  const { pairs, stranded } = useMemo(() => {
    const settlements: SideSettlement[] = []
    /*
     * Bets whose rounds have been changed out from under them.
     *
     * `standingFor` refuses to price one rather than quoting a number for a bet
     * nobody struck — but dropping it from the list without a word is its own
     * kind of wrong, because the money simply vanishes off the screen of the
     * man who is owed it.
     */
    let stranded = 0
    for (const match of matches) {
      if (match.playerA !== viewerId && match.playerB !== viewerId) continue
      const standing = standingFor(doc, match, scores, confirmations)
      if (standing) settlements.push(standing.settlement)
      else if (!match.voidedAt) stranded += 1
    }
    return { pairs: nettedSideMoney(viewerId, settlements), stranded }
  }, [doc, matches, scores, confirmations, viewerId])

  if (pairs.length === 0 && stranded === 0) return null

  return (
    <>
      <SectionRule>Side money</SectionRule>
      {stranded > 0 && (
        <p className="px-4 py-2.5 text-[13px] leading-snug bg-signal text-paper">
          {stranded === 1 ? 'A side match cannot' : `${stranded} side matches cannot`} be
          settled: a round {stranded === 1 ? 'it was' : 'they were'} struck over has been
          removed or changed since. Work {stranded === 1 ? 'it' : 'them'} out between
          yourselves — the app will not price a bet nobody agreed to.
        </p>
      )}
      <ul className="divide-y divide-rule">
        {pairs.map((p) => (
          <li key={p.playerId} className="px-4 py-2.5 flex items-center gap-3">
            <span className="bc-name text-sm flex-1">
              {playerName(doc, p.playerId)}
              <span className="text-mute text-[12px] ml-1.5">
                {p.matches} match{p.matches === 1 ? '' : 'es'}
                {p.provisional > 0 && ` · ${p.provisional} not settled`}
              </span>
            </span>
            <span className="text-right">
              <span
                className={`bc-num text-base block leading-none ${
                  p.cents < 0 ? 'text-signal' : ''
                }`}
              >
                {p.cents === 0 ? 'square' : dollars(p.cents)}
              </span>
              <span className="text-[11px] text-mute font-semibold uppercase tracking-wider">
                {p.cents === 0 ? 'nothing between you' : p.cents > 0 ? "you're owed" : 'you owe'}
                {p.provisional > 0 ? ' · so far' : ''}
              </span>
            </span>
          </li>
        ))}
      </ul>
      <p className="px-4 pt-2 text-[13px] text-mute leading-snug">
        Between the two of you and nothing to do with the pot — none of this is in the
        standings or the payouts.
        {pairs.some((p) => p.provisional > 0)
          ? ' A figure marked “so far” is off cards nobody has signed yet.'
          : ''}
      </p>
    </>
  )
}
